import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, UserMinusIcon, XCircleIcon, CurrencyDollarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { StaffMember } from '../../types/timesheets';

function centsToDollars(c: number | null): string {
  return c == null ? '' : (c / 100).toFixed(2);
}
function formatRate(c: number | null): string {
  return c == null ? '—' : `$${(c / 100).toFixed(2)}/hr`;
}

interface RateTarget {
  id: string;
  name: string | null;
  email: string;
  hourly_rate_cents: number | null;
}

function RateModal({ target, onClose }: { target: RateTarget; onClose: () => void }) {
  const qc = useQueryClient();
  const [dollars, setDollars] = useState(centsToDollars(target.hourly_rate_cents));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const parsed = dollars.trim() === '' ? null : Math.round(parseFloat(dollars) * 100);
      return api.adminSetStaff(target.id, { isStaff: true, hourlyRateCents: parsed });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not save'),
  });

  const dollarsNum = parseFloat(dollars);
  const valid = dollars.trim() === '' || (!Number.isNaN(dollarsNum) && dollarsNum >= 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Set hourly rate</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XCircleIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-white font-medium">{target.name ?? target.email}</p>
            <p className="text-xs text-slate-400">{target.email}</p>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Hourly rate (NZD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <input type="number" min="0" step="0.01" value={dollars} onChange={(e) => setDollars(e.target.value)}
                placeholder="45.00"
                className="w-full pl-7 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
            </div>
            <p className="text-xs text-slate-500 mt-1">Used to cost their logged hours in reports. Staff never see this.</p>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!valid || save.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddStaffSearch({ existingIds }: { existingIds: Set<string> }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const enabled = search.trim().length >= 2;

  const { data } = useQuery({
    queryKey: ['admin', 'users', 'search', search],
    queryFn: () => api.adminGetUsers({ search, limit: 8 }),
    enabled,
  });

  const addMutation = useMutation({
    mutationFn: (userId: string) => api.adminSetStaff(userId, { isStaff: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] }),
  });

  const results = (data?.users ?? []).filter((u) => !existingIds.has(u.id));

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
      <h2 className="text-sm font-semibold text-white">Add a staff member</h2>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users by name or email…"
          className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
      </div>
      {enabled && (
        <div className="divide-y divide-slate-700/50">
          {results.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">No matching users.</p>
          ) : (
            results.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-white">{u.name ?? u.email}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
                <button onClick={() => addMutation.mutate(u.id)} disabled={addMutation.isPending}
                  className="px-3 py-1.5 text-xs bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors">
                  Add as staff
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function AdminStaffPage() {
  const qc = useQueryClient();
  const [rateTarget, setRateTarget] = useState<RateTarget | null>(null);

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: () => api.adminListStaff(),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => api.adminSetStaff(userId, { isStaff: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] }),
  });

  const existingIds = new Set(staff.map((s: StaffMember) => s.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Staff &amp; Rates</h1>
        <p className="text-slate-400 mt-1">Flag hourly-paid staff and set their rate. Rates feed timesheet cost reports only.</p>
      </div>

      <AddStaffSearch existingIds={existingIds} />

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : staff.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <CurrencyDollarIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No staff yet. Search above to add someone.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Name</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Email</th>
                <th className="text-right px-4 py-3 text-slate-400 font-medium">Hourly rate</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {staff.map((s: StaffMember) => (
                <tr key={s.id} className="hover:bg-slate-700/20 transition-colors">
                  <td className="px-4 py-3 text-white">{s.name ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-300">{s.email}</td>
                  <td className={`px-4 py-3 text-right ${s.hourly_rate_cents == null ? 'text-orange-400' : 'text-slate-300'}`}>
                    {s.hourly_rate_cents == null ? 'No rate set' : formatRate(s.hourly_rate_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setRateTarget(s)}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors" title="Set rate">
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { if (confirm(`Remove ${s.name ?? s.email} from staff?`)) removeMutation.mutate(s.id); }}
                        className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors" title="Remove from staff">
                        <UserMinusIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rateTarget && <RateModal target={rateTarget} onClose={() => setRateTarget(null)} />}
    </div>
  );
}

export default AdminStaffPage;
